import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Html5Qrcode } from "html5-qrcode";
import { X, Camera, SwitchCamera, Upload, AlertCircle, ScanLine, RefreshCw, ShieldAlert, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";

export function parseScannedCode(rawText) {
    if (!rawText) return "";
    let text = String(rawText).trim();

    if (text.startsWith("{") && text.endsWith("}")) {
        try {
            const parsed = JSON.parse(text);
            if (parsed.code) return String(parsed.code).trim().toUpperCase();
            if (parsed.pass_code) return String(parsed.pass_code).trim().toUpperCase();
            if (parsed.visitor_code) return String(parsed.visitor_code).trim().toUpperCase();
        } catch {}
    }

    if (text.startsWith("http://") || text.startsWith("https://")) {
        try {
            const u = new URL(text);
            const queryCode = u.searchParams.get("code") || u.searchParams.get("pass") || u.searchParams.get("id");
            if (queryCode) return queryCode.trim().toUpperCase();
            const lastPart = u.pathname.split("/").filter(Boolean).pop();
            if (lastPart && lastPart.length <= 16) return lastPart.trim().toUpperCase();
        } catch {}
    }

    return text.replace(/['"]/g, "").trim().toUpperCase();
}

function playScanFeedback() {
    try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(880, audioCtx.currentTime);
        gain.gain.setValueAtTime(0.25, audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.18);
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.start();
        osc.stop(audioCtx.currentTime + 0.18);
    } catch {}

    if (typeof navigator !== "undefined" && navigator.vibrate) {
        try {
            navigator.vibrate([80, 40, 80]);
        } catch {}
    }
}

export default function QRScannerModal({ open, onClose, onScan, seniorMode = true, lang = "en" }) {
    const isHi = lang === "hi";
    const scannerRef = useRef(null);
    const fileInputRef = useRef(null);
    const containerId = "guard-qr-reader-container";

    const [isScanning, setIsScanning] = useState(false);
    const [cameraFacing, setCameraFacing] = useState("environment"); // "environment" or "user"
    const [cameras, setCameras] = useState([]);
    const [permissionDenied, setPermissionDenied] = useState(false);
    const [isInsecureHttp, setIsInsecureHttp] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [decodingFile, setDecodingFile] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [showHelp, setShowHelp] = useState(false);

    const stopScanner = async () => {
        if (scannerRef.current) {
            try {
                if (scannerRef.current.isScanning) {
                    await scannerRef.current.stop();
                }
                scannerRef.current.clear();
            } catch (e) {
                console.warn("Error stopping QR scanner:", e);
            }
            scannerRef.current = null;
        }
        setIsScanning(false);
    };

    const handleScannedResult = (decodedText) => {
        if (processing) return;
        setProcessing(true);
        playScanFeedback();

        const cleanCode = parseScannedCode(decodedText);
        toast.success(`Scanned Pass: ${cleanCode}`);

        stopScanner().finally(() => {
            onScan(cleanCode);
            onClose();
        });
    };

    const requestPermissionAndStart = async () => {
        setErrorMsg("");
        setPermissionDenied(false);
        setIsInsecureHttp(false);
        await stopScanner();

        // 1. Check if browser environment supports getUserMedia (Secure Context Check)
        const isSecure = typeof window !== "undefined" && (
            window.isSecureContext ||
            window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1"
        );

        if (!isSecure && (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia)) {
            setIsInsecureHttp(true);
            setErrorMsg("Browser blocked live camera stream because connection is HTTP. Tap 'Snap QR with Camera' below, which works immediately!");
            return;
        }

        // 2. Explicitly probe camera permission first with native getUserMedia
        try {
            if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
                const probeStream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: { ideal: cameraFacing } },
                    audio: false
                });
                // Permission granted! Stop probe tracks immediately
                probeStream.getTracks().forEach((track) => track.stop());
            }
        } catch (permErr) {
            console.warn("Initial camera permission probe failed:", permErr);
            const msg = String(permErr?.name || permErr?.message || permErr || "");
            if (msg.includes("NotAllowedError") || msg.includes("Permission") || msg.includes("SecurityError")) {
                setPermissionDenied(true);
                setErrorMsg("Camera permission is blocked in your browser. Please allow camera access or use the 1-Tap Camera Snapshot below.");
                return;
            }
        }

        // 3. Start Html5Qrcode with video container
        try {
            const scanner = new Html5Qrcode(containerId);
            scannerRef.current = scanner;

            const devices = await Html5Qrcode.getCameras().catch(() => []);
            setCameras(devices || []);

            await scanner.start(
                { facingMode: cameraFacing },
                {
                    fps: 12,
                    qrbox: (viewfinderWidth, viewfinderHeight) => {
                        const edge = Math.min(viewfinderWidth, viewfinderHeight) * 0.72;
                        return { width: Math.floor(edge), height: Math.floor(edge) };
                    },
                    aspectRatio: 1.0,
                },
                (decodedText) => {
                    handleScannedResult(decodedText);
                },
                () => {
                    // ignore per-frame parse failures
                }
            );

            setIsScanning(true);
        } catch (err) {
            console.error("Camera scanner start error:", err);
            const msg = String(err?.message || err || "");
            if (msg.includes("NotAllowedError") || msg.includes("Permission") || msg.includes("denied")) {
                setPermissionDenied(true);
                setErrorMsg("Camera permission was denied. Tap 'Snap QR with Camera' below or allow camera in browser site settings.");
            } else if (msg.includes("NotFoundError") || msg.includes("device not found")) {
                setErrorMsg("No camera device was detected. Tap 'Snap QR with Camera' or upload an image below.");
            } else {
                setErrorMsg("Could not start live camera feed. Use 'Snap QR with Camera' to scan immediately.");
            }
            setIsScanning(false);
        }
    };

    const toggleFacing = () => {
        setCameraFacing((prev) => (prev === "environment" ? "user" : "environment"));
    };

    // Handle instant photo snap or image upload
    const handleFileScan = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setDecodingFile(true);
        setErrorMsg("");
        try {
            const scanner = scannerRef.current || new Html5Qrcode(containerId, { verbose: false });
            const decodedResult = await scanner.scanFile(file, true);
            handleScannedResult(decodedResult);
        } catch (err) {
            console.warn("QR file scan error:", err);
            toast.error("Could not find a valid QR code in this image");
            setErrorMsg("No readable QR code found in the image. Please point clearly at the QR code and try again.");
        } finally {
            setDecodingFile(false);
            if (e.target) e.target.value = "";
        }
    };

    const triggerNativeCamera = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    useEffect(() => {
        if (open) {
            setProcessing(false);
            const t = setTimeout(() => {
                requestPermissionAndStart();
            }, 150);
            return () => {
                clearTimeout(t);
                stopScanner();
            };
        } else {
            stopScanner();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, cameraFacing]);

    if (!open) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3 sm:p-4 animate-in fade-in duration-200"
            onClick={() => {
                stopScanner();
                onClose();
            }}
            data-testid="qr-scanner-modal"
        >
            <div
                className="bg-neutral-900 text-white rounded-3xl border border-neutral-700 w-full max-w-md overflow-hidden shadow-2xl flex flex-col relative max-h-[94vh]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 px-5 border-b border-neutral-800 bg-neutral-900/90">
                    <div className="flex items-center gap-2.5">
                        <span className={`rounded-xl bg-nest-clay/20 text-nest-clay grid place-items-center ${seniorMode ? "h-11 w-11" : "h-9 w-9"}`}>
                            <ScanLine size={seniorMode ? 24 : 20} />
                        </span>
                        <div>
                            <h3 className={`font-display font-bold ${seniorMode ? "text-xl sm:text-2xl" : "text-lg"}`}>
                                {isHi ? "गेट पास QR स्कैनर" : "Gate Pass QR Scanner"}
                            </h3>
                            <p className={`${seniorMode ? "text-xs text-neutral-300" : "text-[11px] text-neutral-400"}`}>
                                {isHi ? "Visitor या स्टाफ का पास स्कैन करें" : "Scan visitor or staff gate pass"}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            stopScanner();
                            onClose();
                        }}
                        className={`grid place-items-center rounded-full hover:bg-neutral-800 text-neutral-400 hover:text-white transition cursor-pointer ${
                            seniorMode ? "h-11 w-11" : "h-8 w-8"
                        }`}
                        data-testid="close-scanner-btn"
                        title={isHi ? "बंद करें" : "Close"}
                    >
                        <X size={seniorMode ? 24 : 18} />
                    </button>
                </div>

                {/* Hidden File Input for Native Camera Snapshot */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileScan}
                    className="hidden"
                    data-testid="native-camera-qr-input"
                />

                {/* Viewfinder / Scanner Area */}
                <div className="relative bg-black flex items-center justify-center min-h-[300px] sm:min-h-[340px] max-h-[420px] overflow-hidden">
                    <div id={containerId} className="w-full h-full min-h-[300px] max-h-[420px] flex items-center justify-center" />

                    {/* Animated Scanning Beam & Targeting Frame */}
                    {isScanning && !errorMsg && (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                            <div className="relative h-56 w-56 sm:h-64 sm:w-64 rounded-2xl border-2 border-dashed border-nest-clay/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.55)]">
                                <div className="absolute -top-1 -left-1 h-6 w-6 border-t-4 border-l-4 border-nest-clay rounded-tl-lg" />
                                <div className="absolute -top-1 -right-1 h-6 w-6 border-t-4 border-r-4 border-nest-clay rounded-tr-lg" />
                                <div className="absolute -bottom-1 -left-1 h-6 w-6 border-b-4 border-l-4 border-nest-clay rounded-bl-lg" />
                                <div className="absolute -bottom-1 -right-1 h-6 w-6 border-b-4 border-r-4 border-nest-clay rounded-br-lg" />
                                <div className="absolute left-2 right-2 h-0.5 bg-gradient-to-r from-transparent via-nest-clay to-transparent shadow-[0_0_12px_#D95A2B] animate-pulse top-1/2 -translate-y-1/2" />
                            </div>
                        </div>
                    )}

                    {/* Camera Control Overlay */}
                    {isScanning && !errorMsg && (
                        <div className="absolute top-3 right-3 flex items-center gap-2">
                            <button
                                type="button"
                                onClick={toggleFacing}
                                className="h-9 w-9 rounded-full bg-black/60 hover:bg-black/90 text-white backdrop-blur-md grid place-items-center border border-white/20 transition cursor-pointer"
                                title={`Switch to ${cameraFacing === "environment" ? "Front" : "Rear"} Camera`}
                                data-testid="flip-camera-btn"
                            >
                                <SwitchCamera size={16} />
                            </button>
                        </div>
                    )}

                    {/* Permission Issue / Fallback Overlay */}
                    {errorMsg && (
                        <div className="absolute inset-0 bg-neutral-900/98 flex flex-col items-center justify-center p-6 text-center z-10 overflow-y-auto">
                            <div className="h-12 w-12 rounded-2xl bg-nest-clay/20 text-nest-clay grid place-items-center mb-3">
                                {permissionDenied ? <ShieldAlert size={28} /> : <Camera size={28} />}
                            </div>

                            <h4 className="font-semibold text-lg">
                                {permissionDenied ? "Camera Permission Blocked" : isInsecureHttp ? "HTTP Connection Detected" : "Camera Access Issue"}
                            </h4>

                            <p className="text-xs text-neutral-300 mt-1.5 max-w-xs leading-relaxed">
                                {errorMsg}
                            </p>

                            {/* 1-TAP INSTANT SNAPSHOT BUTTON (Bypasses browser permission completely) */}
                            <div className="mt-5 w-full max-w-xs space-y-2.5">
                                <button
                                    type="button"
                                    onClick={triggerNativeCamera}
                                    disabled={decodingFile}
                                    className="w-full bg-nest-clay hover:bg-nest-clayDark text-white py-3.5 px-4 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg active:scale-95 transition cursor-pointer"
                                    data-testid="fallback-snap-camera-btn"
                                >
                                    <Camera size={18} />
                                    <span>{decodingFile ? "Reading QR Code…" : "📸 Snap QR with Phone Camera"}</span>
                                </button>
                                <p className="text-[11px] text-neutral-400">
                                    ✓ Opens your phone's camera directly & doesn't require browser permissions.
                                </p>
                            </div>

                            {/* Permission Helper Accordion */}
                            <div className="mt-4 pt-3 border-t border-neutral-800 w-full max-w-xs text-left">
                                <button
                                    type="button"
                                    onClick={() => setShowHelp(!showHelp)}
                                    className="text-xs font-semibold text-neutral-400 hover:text-white flex items-center justify-between w-full"
                                >
                                    <span className="flex items-center gap-1.5">
                                        <Info size={13} className="text-nest-clay" /> How to allow live camera
                                    </span>
                                    <span>{showHelp ? "▲" : "▼"}</span>
                                </button>

                                {showHelp && (
                                    <div className="mt-2 text-[11px] text-neutral-300 space-y-1.5 bg-neutral-800/60 p-3 rounded-xl border border-neutral-700/60">
                                        <p><strong>Chrome / Android:</strong></p>
                                        <ol className="list-decimal list-inside pl-1 space-y-0.5 text-neutral-400">
                                            <li>Tap the <strong>Lock / Settings icon 🔒</strong> in your address bar.</li>
                                            <li>Tap <strong>Permissions</strong> → <strong>Camera</strong>.</li>
                                            <li>Select <strong>Allow</strong>, then tap Try Again below.</li>
                                        </ol>
                                        <p className="pt-1"><strong>iPhone / Safari:</strong></p>
                                        <p className="text-neutral-400 pl-1">Tap <strong>"aA"</strong> in address bar → <strong>Website Settings</strong> → <strong>Camera</strong> → <strong>Allow</strong>.</p>
                                    </div>
                                )}
                            </div>

                            <div className="mt-4 flex gap-2">
                                <button
                                    type="button"
                                    onClick={requestPermissionAndStart}
                                    className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-semibold inline-flex items-center gap-1.5 transition cursor-pointer"
                                >
                                    <RefreshCw size={13} /> Try Live Camera Again
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Controls */}
                <div className="p-4 px-5 border-t border-neutral-800 bg-neutral-900/90 flex flex-col gap-3">
                    <div className="flex items-center justify-between text-xs text-neutral-400">
                        <span className={seniorMode ? "text-xs font-semibold text-neutral-300" : "text-xs"}>
                            {isHi ? "QR कोड को फ्रेम के बीच में रखें" : "Align QR code in center of viewfinder"}
                        </span>
                        {cameras.length > 1 && !errorMsg && (
                            <button
                                type="button"
                                onClick={toggleFacing}
                                className="text-nest-clay hover:underline inline-flex items-center gap-1 cursor-pointer font-bold"
                            >
                                <SwitchCamera size={14} /> <span>{isHi ? "कैमरा बदलें" : "Switch Camera"}</span>
                            </button>
                        )}
                    </div>

                    {/* Instant Fallback & Upload Actions */}
                    <div className="pt-2 border-t border-neutral-800/80 flex items-center justify-between gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={triggerNativeCamera}
                            disabled={decodingFile}
                            className={`inline-flex items-center gap-1.5 font-bold text-white rounded-xl bg-neutral-800 hover:bg-neutral-700 border border-neutral-700 transition cursor-pointer ${
                                seniorMode ? "h-11 px-4 text-xs sm:text-sm" : "text-xs px-3.5 py-2"
                            }`}
                            title="Take photo using phone camera"
                            data-testid="bottom-snap-camera-btn"
                        >
                            <Camera size={seniorMode ? 18 : 14} className="text-nest-clay" />
                            <span>{decodingFile ? (isHi ? "प्रक्रिया जारी…" : "Processing…") : (isHi ? "फोटो खींचें" : "Take Photo")}</span>
                        </button>

                        <label className={`cursor-pointer inline-flex items-center gap-1.5 font-bold text-neutral-300 hover:text-white rounded-xl hover:bg-neutral-800 border border-transparent hover:border-neutral-700 transition ${
                            seniorMode ? "h-11 px-4 text-xs sm:text-sm" : "text-xs px-3 py-2"
                        }`}>
                            <Upload size={seniorMode ? 18 : 14} className="text-neutral-400" />
                            <span>{isHi ? "गैलरी से" : "From Gallery"}</span>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleFileScan}
                                className="hidden"
                                data-testid="upload-qr-gallery-input"
                            />
                        </label>

                        <button
                            type="button"
                            onClick={() => {
                                stopScanner();
                                onClose();
                            }}
                            className={`font-bold rounded-xl bg-neutral-800 hover:bg-neutral-700 text-neutral-300 hover:text-white transition cursor-pointer ${
                                seniorMode ? "h-11 px-5 text-xs sm:text-sm" : "text-xs px-4 py-2"
                            }`}
                        >
                            {isHi ? "रद्द करें" : "Cancel"}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
